<template>
  <svg :width="width" :height="height" viewBox="0 0 600 250"></svg>
</template>

<script>
import {
  forceSimulation,
  forceX,
  forceY,
  forceCollide,
  forceManyBody
} from "d3-force";
import { scaleBand, scaleOrdinal } from "d3-scale";
import { select, mouse } from "d3-selection";
import { range } from "d3-array";

export default {
  props: {
    width: {
      type: Number,
      default: 600
    }
  },
  computed: {
    height() {
      return (this.width / 600) * 250;
    }
  },
  mounted() {
    const margin = 65;
    const bandwidth = 20;
    const padding = bandwidth * 0.25;
    const step = bandwidth + padding;

    const xCount = 5 * 4 - 1;
    const yCount = 5;
    const width = step * xCount - padding;
    const height = step * yCount - padding;

    const chargeForceStrength = -100;

    // let cursorX, cursorY

    const svg = select(this.$el)
      .on("mouseenter touchstart", mouseenter, { passive: true })
      .on("mousemove touchmove", mousemove, { passive: true })
      .on("mouseleave touchend", mouseleave, { passive: true });

    const g = svg.append("g");

    const logoData = ["P", "N", "G", "K"];

    const letterData = [
      { xPos: -1, yPos: -1, letter: "" }, // Invisible dot
      { xPos: 0, yPos: 0, letter: "P" },
      { xPos: 0, yPos: 1, letter: "P" },
      { xPos: 0, yPos: 2, letter: "P" },
      { xPos: 0, yPos: 3, letter: "P" },
      { xPos: 0, yPos: 4, letter: "P" },
      { xPos: 1, yPos: 0, letter: "P" },
      { xPos: 1, yPos: 2, letter: "P" },
      { xPos: 2, yPos: 0, letter: "P" },
      { xPos: 2, yPos: 2, letter: "P" },
      { xPos: 3, yPos: 0, letter: "P" },
      { xPos: 3, yPos: 1, letter: "P" },
      { xPos: 3, yPos: 2, letter: "P" },
      { xPos: 0, yPos: 0, letter: "N" },
      { xPos: 0, yPos: 1, letter: "N" },
      { xPos: 0, yPos: 2, letter: "N" },
      { xPos: 0, yPos: 3, letter: "N" },
      { xPos: 0, yPos: 4, letter: "N" },
      { xPos: 1, yPos: 1, letter: "N" },
      { xPos: 2, yPos: 2, letter: "N" },
      { xPos: 3, yPos: 0, letter: "N" },
      { xPos: 3, yPos: 1, letter: "N" },
      { xPos: 3, yPos: 2, letter: "N" },
      { xPos: 3, yPos: 3, letter: "N" },
      { xPos: 3, yPos: 4, letter: "N" },
      { xPos: 0, yPos: 0, letter: "G" },
      { xPos: 0, yPos: 1, letter: "G" },
      { xPos: 0, yPos: 2, letter: "G" },
      { xPos: 0, yPos: 3, letter: "G" },
      { xPos: 0, yPos: 4, letter: "G" },
      { xPos: 1, yPos: 0, letter: "G" },
      { xPos: 1, yPos: 4, letter: "G" },
      { xPos: 2, yPos: 0, letter: "G" },
      { xPos: 2, yPos: 2, letter: "G" },
      { xPos: 2, yPos: 4, letter: "G" },
      { xPos: 3, yPos: 0, letter: "G" },
      { xPos: 3, yPos: 2, letter: "G" },
      { xPos: 3, yPos: 3, letter: "G" },
      { xPos: 3, yPos: 4, letter: "G" },
      { xPos: 0, yPos: 0, letter: "K" },
      { xPos: 0, yPos: 1, letter: "K" },
      { xPos: 0, yPos: 2, letter: "K" },
      { xPos: 0, yPos: 3, letter: "K" },
      { xPos: 0, yPos: 4, letter: "K" },
      { xPos: 1, yPos: 2, letter: "K" },
      { xPos: 2, yPos: 1, letter: "K" },
      { xPos: 2, yPos: 3, letter: "K" },
      { xPos: 3, yPos: 0, letter: "K" },
      { xPos: 3, yPos: 4, letter: "K" }
    ];

    const root = letterData[0];

    const xScale = scaleBand()
      .domain(range(xCount))
      .range([margin, margin + width])
      .paddingInner(padding / step);

    const yScale = scaleBand()
      .domain(range(yCount))
      .range([margin, margin + height])
      .paddingInner(padding / step);

    const letterScale = scaleOrdinal()
      .domain(logoData)
      .range(range(logoData.length).map(d => d * 5));

    letterData.forEach((d, i) => {
      if (i === 0) {
        d.xPosPixel = -9999;
        d.yPosPixel = -9999;
        d.x = -9999;
        d.y = -9999;
      } else {
        d.xPosPixel = xScale(letterScale(d.letter) + d.xPos);
        d.yPosPixel = yScale(d.yPos);
        d.x = d.xPosPixel;
        d.y = d.yPosPixel;
      }
    });

    const simulation = forceSimulation(letterData)
      .velocityDecay(0.1)
      .force(
        "x",
        forceX((d, i) => (i === 0 ? 0 : d.xPosPixel)).strength((d, i) =>
          i === 0 ? 0 : 0.05
        )
      )
      .force(
        "y",
        forceY((d, i) => (i === 0 ? 0 : d.yPosPixel)).strength((d, i) => {
          return i === 0 ? 0 : 0.05;
        })
      )
      .force("charge", forceManyBody().strength(0))
      .force(
        "collide",
        forceCollide()
          .radius(bandwidth / 2)
          .iterations(2)
      )
      .on("tick", ticked);

    const rect = g
      .selectAll("rect")
      .data(letterData)
      .enter()
      .append("rect")
      .attr("width", (d, i) => (i === 0 ? 0 : bandwidth))
      .attr("height", (d, i) => (i === 0 ? 0 : bandwidth))
      .attr("fill", (d, i) => (i === 0 ? "none" : "#ffffff"));

    function ticked() {
      rect.attr("x", d => d.x).attr("y", d => d.y);
    }

    function mousemove() {
      const pos = mouse(this);
      // cursorX = pos[0]
      // cursorY = pos[1]

      root.x = pos[0];
      root.y = pos[1];
      simulation.alpha(1);
      simulation.restart();
    }

    function mouseenter() {
      simulation
        .force("charge")
        .strength((d, i) => (i === 0 ? chargeForceStrength : 0));
    }

    function mouseleave() {
      // cursorX = null
      // cursorY = null
      simulation.force("charge").strength(0);
    }
  }
};
</script>
